import { Injectable } from '@angular/core';
import { InvestigationFormEntity } from '../../domain/entities/investigation-form.entity';
import { InvestigationFormResponseDto } from '../dtos/investigation-form-response.dto';
import { UpdateInvestigationFormRequestDto } from '../dtos/update-investigation-form-request.dto';
import { FormVersionDetailResponseDto } from '../dtos/form-version-detail-response.dto';
import { FormVersionDetailRepositoryResponseDto } from '../dtos/form-version-detail-repository-response.dto';
import { FieldConstraintTypeResponseDto, FieldTypeResponseDto } from '../dtos/field-constraint-type-response.dto';

@Injectable({ providedIn: 'root' })
export class InvestigationFormMapper {
  public toDomain(dto: InvestigationFormResponseDto): InvestigationFormEntity {
    const entity = new InvestigationFormEntity();
    const mappedStatus = this._mapStatus(dto.status);

    entity.id = dto.id;
    entity.name = dto.name;
    entity.description = dto.description ?? '';
    entity.investigationTypeId = dto.investigationTypeId;
    entity.investigationTypeName = dto.investigationTypeName;
    entity.status = mappedStatus;
    entity.statusName = mappedStatus;
    entity.publishedVersionId = dto.publishedVersionId;
    entity.publishedVersionNumber = dto.publishedVersionNumber;
    entity.fieldCount = dto.fieldCount;
    entity.createdAt = new Date(dto.createdOn);
    entity.updatedAt = new Date(dto.updatedOn);
    return entity;
  }

  private _mapStatus(status: number): 'Draft' | 'Published' | 'Archived' {
    switch (status) {
      case 0:
        return 'Draft';
      case 1:
        return 'Published';
      case 2:
        return 'Archived';
      default:
        return 'Draft';
    }
  }

  public toDomainArray(dtos: InvestigationFormResponseDto[]): InvestigationFormEntity[] {
    return dtos.map((dto) => this.toDomain(dto));
  }

  public toUpdateDto(params: UpdateInvestigationFormParams): UpdateInvestigationFormRequestDto {
    return {
      name: params.name,
      description: params.description,
      investigationTypeId: params.investigationTypeId,
    };
  }

  public mapFormVersionDetail(dto: FormVersionDetailResponseDto): FormVersionDetailRepositoryResponseDto {
    const mappedStatus = this._mapStatus(dto.status);
    return {
      ...dto,
      statusName: mappedStatus,
    };
  }

  public static flattenFieldTypeConstraints(fieldTypes: FieldTypeResponseDto[]): FieldConstraintTypeResponseDto[] {
    const flattened: FieldConstraintTypeResponseDto[] = [];

    for (const fieldType of fieldTypes) {
      for (const constraint of fieldType.constraints) {
        let defaultValue: string | number | boolean | null = constraint.defaultValue;

        if (constraint.defaultValue !== null) {
          switch (constraint.valueType) {
            case 'integer':
              defaultValue = parseInt(constraint.defaultValue, 10);
              break;
            case 'boolean':
              defaultValue = constraint.defaultValue === 'true';
              break;
            case 'string':
            case 'jsonb':
              defaultValue = constraint.defaultValue;
              break;
          }
        }

        flattened.push({
          id: constraint.typeConstraintId,
          fieldType: fieldType.fieldType,
          constraintKey: constraint.constraintKey,
          valueType: constraint.valueType,
          defaultValue,
          label: constraint.label,
          sortOrder: constraint.sortOrder,
        });
      }
    }

    return flattened;
  }
}

export interface UpdateInvestigationFormParams {
  name?: string;
  description?: string;
  investigationTypeId?: string;
}
